<script setup lang="ts">
import { ContentBlock } from '~/components/ui/content-block'
import { WidgetBankPageAboutBankHeader, WidgetBankPageAboutBankHeaderSkeleton } from '~/components/widget/bank-page'
import type { IBank } from '~/components/entity/bank'
import { Content } from '~/components/ui/content'
import { WidgetBankServicesMenu } from '~/components/widget/bank'
import { navItems } from '~/data/pages/banks/mocks'

const props = defineProps<{
  bank?: IBank
}>()

const { t } = useI18n()

const route = useRoute()
const config = useRuntimeConfig()

const pageUrl = `${config.public.assetUrl}${route.path}`

const bankLinks = computed(() => {
  if (!props.bank) return []

  const links = []

  if (props.bank.website_url) {
    links.push({
      href: props.bank.website_url,
      text: props.bank.website_url,
      target: '_blank',
      rel: 'noopener'
    })
  }

  if (props.bank.email) {
    links.push({
      href: `mailto:${props.bank.email}`,
      text: props.bank.email,
      target: '_blank',
      rel: 'noopener'
    })
  }

  if (props.bank.banking_url) {
    links.push({
      href: props.bank.banking_url,
      text: t('widget.bank_page.about_bank.internet_banking'),
      target: '_blank',
      rel: 'noopener'
    })
  }

  if (props.bank.central_bank_page_url) {
    links.push({
      href: props.bank.central_bank_page_url,
      text: t('widget.bank_page.about_bank.nbkr_info'),
      target: '_blank',
      rel: 'noopener'
    })
  }

  return links
})

const getAlignmentClass = (index: number, total: number) => {
  if (total === 1) return ''
  if (total === 2) return index === 1 ? 'sm:text-center' : ''
  if (total === 3) return index === 1 ? 'sm:text-center' : index === 2 ? 'sm:text-right' : ''
  // For 4 items (original case)
  if (index === 1) return 'sm:text-center'
  if (index === 2) return 'sm:text-center'
  if (index === 3) return 'sm:text-right'
  return ''
}

if (props.bank) {
  useSchemaOrg([
    defineOrganization({
      // Own @id, otherwise the bank is merged into the site identity (#identity)
      '@id': `${pageUrl}#organization`,
      name: props.bank.title,
      alternateName: props.bank.official_title || undefined,
      description: useStripTags(props.bank.description),
      logo: {
        '@type': 'ImageObject',
        url: props.bank.logo,
        caption: props.bank.title
      },
      image: props.bank.logo,
      address: {
        '@type': 'PostalAddress',
        postalCode: props.bank.postal_code || '',
        streetAddress: props.bank.head_branch_location || '',
        addressLocality: props.bank.head_branch_location || '',
        addressCountry: 'KG'
      },
      telephone: props.bank.call_center_phone || undefined,
      email: props.bank.email || undefined,
      url: props.bank.website_url || pageUrl,
      sameAs: [
        props.bank.website_url,
        props.bank.banking_url,
        props.bank.central_bank_page_url
      ].filter(Boolean),
      '@type': ['Organization', 'FinancialService'],
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: t('schema.bank_page.about_bank.bank_services'),
        itemListElement: [
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'BankAccount',
              name: t('schema.bank_page.about_bank.bank_accounts')
            }
          },
          { '@type': 'Offer', itemOffered: { '@type': 'LoanOrCredit', name: t('common.credits') } },
          {
            '@type': 'Offer',
            itemOffered: { '@type': 'DepositAccount', name: t('common.deposits_label') }
          }
        ]
      }
    })
  ])
}
</script>

<template>
  <div class="space-y-5">
    <template v-if="!props.bank || Object.keys(props.bank).length === 0">
      <ContentBlock variant="white">
        <WidgetBankPageAboutBankHeaderSkeleton />
      </ContentBlock>
    </template>
    <template v-else>
      <WidgetBankPageAboutBankHeader :bank="props.bank">
        <WidgetBankServicesMenu
          v-if="props.bank.status === 'active'"
          :organization="props.bank"
          :nav-items="navItems"
        />
      </WidgetBankPageAboutBankHeader>

      <ContentBlock variant="white">
        <Content>
          <h2 class="h4">{{ $t('common.about_bank') }}</h2>
          <div v-hydrated-html="props.bank?.description" />
        </Content>
        <div
          v-if="bankLinks.length"
          class="flex flex-col justify-between space-y-3 border-t pt-4 text-xs leading-3 sm:grid sm:grid-cols-4 sm:space-y-0 sm:pt-6"
        >
          <div
            v-for="(link, index) in bankLinks"
            :key="index"
            :class="getAlignmentClass(index, bankLinks.length)"
          >
            <a
              class="font-semibold text-blue-600 hover:underline"
              :href="link.href"
              :target="link.target"
              :rel="link.rel"
              >{{ link.text }}</a
            >
          </div>
        </div>
      </ContentBlock>
    </template>
  </div>
</template>
